import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AppContext } from "Context/AppContext";
import { CHANGE_OWNER, CREATE, LIST_CREATE, LIST_DELETE, LIST_READ, LIST_TRANSFER, LIST_UPDATE, LISTS, SEARCH_PLACEHOLDER } from "./constants";
import debounce from "lodash/debounce";
import usePagination from "Components/Common/hooks/usePagination";
import ListTable from "./ListTable";
import Button from "Components/DesignSystem/Button/Button";
import Search from "Components/DesignSystem/Search/Search";
import Typography from "Components/DesignSystem/Typography/Typography";
import Badge from "Components/DesignSystem/Badge/Badge";
import Divider from "Components/DesignSystem/Divider/Divider";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";
import { toast } from "Components/DesignSystem/Toast/useToast";
import CopyModal from "./CopyModal";
import CreateListDialog from "./CreateListDialog";
import { copyListItem, deleteListItem, getList, saveList, transferListOwner } from "./Services/ListServices";
import ChangeOwnerDialog, { User } from "../Rules/ChangeOwnerDialog";
import AuthManager from "Common/AuthManager";

const getCurrentUserId = (): string => {
  const userObj = AuthManager.getAuthUser();
  return userObj?.profile?.id ?? '';
};

const isOwner = (createdByUserId: string): boolean => {
  const currentId = getCurrentUserId();
  if (!createdByUserId || !currentId) return false;
  return String(createdByUserId) === String(currentId);
};

const ListsView = () => {
  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;

  // Pagination — same hook as Rules
  const { totalRecords, handlePageChange, updatePageSize, paginationParams, updateTotalRecords } = usePagination(10);

  // Sort state — mirrors Rules' sortColumn + sortDirection
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  // Search state — mirrors Rules' searchString (debounced)
  const [searchString, setSearchString] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const isSortMounted = useRef(false);
  const [selectedListItem, setSelectedListItem] = useState<{ id: string, name: string } | null>(null);
  const [copyInProgress, setCopyInProgress] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createListInProgress, setCreateListInProgress] = useState<boolean>(false);
  const [changeOwnerTarget, setChangeOwnerTarget] = useState<{ id: string, name: string } | null>(null);
  const [changeOwnerInProgress, setChangeOwnerInProgress] = useState(false);

  const createPermission = appContext?.pm?.checkPermission(LIST_CREATE);
  const readPermission = appContext.pm.checkPermission(LIST_READ);
  const updatePermission = appContext.pm.checkPermission(LIST_UPDATE);
  const deletePermission = appContext.pm.checkPermission(LIST_DELETE);
  const transferPermission = appContext.pm.checkPermission(LIST_TRANSFER);

  const openCreateListDialog = () => {
    if (!createPermission) { navigate("/workspace/module/permission/denied"); return; }
    setShowCreateDialog(true);
  }
  const closeCreateListDialog = () => setShowCreateDialog(false);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchTableData = async () => {
    if (!industryId) return;
    try {
      setLoading(true);
      const res = await getList(
        industryId,
        paginationParams.page,
        paginationParams.per_page,
        searchString,
        sortBy,
        sortOrder
      );
      if (res?.data?.done) {
        setData(res?.data?.data?.lists ?? []);
        updateTotalRecords(res?.data?.data?.total ?? 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Effect 1: Re-fetch when page, per_page, searchString, or industry changes
  useEffect(() => {
    if (!readPermission) {
      navigate("/workspace/module/permission/denied");
      return;
    }
    if (industryId) fetchTableData();
  }, [appContext.channelKey, paginationParams.page, paginationParams.per_page, searchString]);

  // Effect 2: When sort changes, reset to page 1 then fetch 
  // Skip on initial mount — Effect 1 already handles the first fetch
  useEffect(() => {
    if (!isSortMounted.current) {
      isSortMounted.current = true;
      return;
    }
    if (paginationParams.page !== 1) {
      handlePageChange(1);
    } else {
      fetchTableData();
    }
  }, [sortBy, sortOrder]);

  // ── Sort ────────────────────────────────────────────────────────────────
  const handleSort = (column: string) => {
    const newDirection: 'ASC' | 'DESC' =
      sortBy === column ? (sortOrder === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
    setSortBy(column);
    setSortOrder(newDirection);
  };

  // ── Search — 500ms debounce ───────────────────────────────
  const debouncedSearch = useMemo(
    () => debounce((value: string) => {
      setSearchString(value);
      handlePageChange(1);
    }, 500),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  // ── Row handlers (data is already current-page slice from server) ────────
  const handleEdit = (index: number) => {
    const list: any = data[index];
    if ((isOwner(list?.created_by_userid) || !list?.created_by_userid) && updatePermission) {
      navigate(`/gms/lists/edit/${list?.id}`);
    } else {
      navigate(`/gms/lists/view/${list?.id}`);
    }
  };

  const handleDelete = async (index: number) => {
    const list: any = data[index];
    if (!deletePermission) {
      toast.info("You don't have the permission to andledelete");
      return;
    }
    if (!isOwner(list?.created_by_userid)) {
      toast.info("Only the owner can delete this list");
      return;
    }
    const deleteRes = await deleteListItem(list?.id);
    if (deleteRes) {
      toast.success("Deleted Successfully");
      await fetchTableData();
    } else toast.warning("Delete Unsuccessful");
  };

  const createCopy = async () => {
    if (selectedListItem == null) return;
    if (createPermission) {
      setCopyInProgress(true);
      const res = await copyListItem(selectedListItem?.id, selectedListItem?.name);
      setCopyInProgress(false);
      setSelectedListItem(null);
      if (res) {
        toast.success(res?.message ?? "Copied Successfully");
        await fetchTableData();
      } else toast.error(res?.message ?? "Copy Unsuccessful");
    } else toast.info("You don't have the permission to create list");
  };

  const handleCopy = (index: number) => {
    if (createPermission) {
      const list: any = data[index];
      setSelectedListItem({ id: list?.id, name: "Copy of " + list?.name });
    } else toast.info("You don't have the permission to create copy");
  };

  const handleCreate = () => {
    if (createPermission) openCreateListDialog();
    else toast.info("You dont have permission to create");
  };

  const handleSave = async (listName: string) => {
    try {
      if (createPermission) {
        if (listName && industryId) {
          setCreateListInProgress(true);
          const requestBody = { name: listName, industry_id: industryId };
          const save = await saveList(requestBody);
          if (save?.data?.done === true) {
            toast.success(save?.data?.message ?? "List created successfully");
            closeCreateListDialog();
            if (save?.data?.data?.id) {
              navigate(`/gms/lists/edit/${save?.data?.data?.id}`);
            }
          } else {
            toast.error(save?.data?.message ?? "Failed to create list");
          }
        } else {
          toast.info("List name is mandatory");
        }
      } else toast.info("You don't have the permission to create list");
    } catch (error) {
      console.log(error);
      toast.error("Failed to create list");
    } finally {
      setCreateListInProgress(false);
    }
  };

  const handleChangeOwnerClick = (index: number) => {
    if (!transferPermission) {
      toast.info("You don't have the permission to transfer ownership");
      return;
    }
    const list: any = data[index];
    setChangeOwnerTarget({ id: list?.id, name: list?.name });
  };

  const handleChangeOwnerSubmit = async (user: User) => {
    if (!changeOwnerTarget) return;
    try {
      setChangeOwnerInProgress(true);
      const username = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
      const res = await transferListOwner(changeOwnerTarget.id, user.id, username);
      if (res?.done) {
        toast.success(`Owner changed to ${username}`);
        setChangeOwnerTarget(null);
        await fetchTableData();
      } else {
        toast.error(res?.message ?? "Failed to change owner");
      }
    } catch (error) {
      toast.error("Failed to change owner");
    } finally {
      setChangeOwnerInProgress(false);
    }
  };

  return (
    <>
      {/* § 273 — no `tw-px-[21px]`, and no `tw-justify-between`.

          The inset made this header an island: its content ran 21 → 1219 of the
          1240px page while the divider, the grid and the pagination all ran 0 → 1240,
          so the h1 sat 8px right of the "Title" column it heads and Create's right
          edge stopped 21px short of the grid's. Same defect as §§ 266-268; the page
          below was already consistent, so this is one token.

          `justify-between` distributed TWO children; the search moving out of the
          action cluster makes three, and it would have spread all three evenly. The
          spacer states which gap is elastic. */}
      <div className={`tw-flex tw-items-center tw-mt-2`} data-testid="lists-header">
        <div className="tw-flex tw-items-center tw-gap-3">
          {/* Was a <label>: the page had no h1 at all. */}
          <Typography variant="display" as="h1" className="tw-text-ink" data-testid="lists-page-title">
            {LISTS}
          </Typography>
          {/* § 273 — `neutral`, not the default brand pill. 80 is a TOTAL — how many
              rows are below — not a queue or a backlog, and the pill rendered the exact
              same `rgb(3, 5, 98)` as the Create button beside it, so the header named
              two destinations of equal weight and one of them was not clickable. Badge
              § 208 defines `neutral` for precisely this: "a number that informs rather
              than demands". The count still reads, at 9.30:1 in light and 7.08:1 in
              dark. (§ 265 met the same defect from the other side, where the Rule
              Action Queue wore the brand pill for a count of ZERO.) */}
          <Badge variant="neutral" data-testid="lists-count-badge">{totalRecords}</Badge>
        </div>
        {/* § 273 — the search sits with the TITLE. It filters the grid below, so it
            answers "what am I looking at", not "what can I do"; in the action cluster
            it read as a second action, and left 815px of dead space — 66% of the row,
            the most of any header in this arc. 288px (`tw-w-72`) is the width Guide,
            Compatibility, Manage Party and Category List all search at; it was 212.
            DOM order is unchanged — title, badge, search, Create — so tab order is
            exactly what it was. */}
        <div className="tw-w-72 tw-min-w-0 tw-ms-4">
          <Search
            placeholder={SEARCH_PLACEHOLDER}
            value={searchInput}
            onChange={handleSearchChange}
            /* § 288 — `loading` is the list fetch (the same flag the table below
               takes); the inequality is the 500ms debounce in front of it. Neither
               term covers the other: the flag starts with the request, one debounce
               after the keystroke the user is watching. */
            loading={loading || searchInput !== searchString}
            loadingLabel="Searching lists"
            size="md"
            data-testid="lists-search-input"
          />
        </div>
        <div className="tw-flex tw-flex-grow" />
        {/* Create is the only action here, so there is no group to break away from —
            the one place this header differs from the other four. */}
        <Button variant="primary" className="tw-shrink-0" disabled={!createPermission} onClick={handleCreate} data-testid="lists-create-btn">{CREATE}</Button>
      </div>
      <Divider className="tw-mt-2" data-testid="lists-header-divider" />
      <ListTable
        listData={data}
        loading={loading}
        emptyContent={
          <EmptyState
            illustration={<NoDataIllustration />}
            title="No Data Found"
            action={<Button variant="primary" onClick={handleCreate} data-testid="lists-empty-create-btn">{CREATE}</Button>}
            data-testid="lists-empty-state"
          />
        }
        handleClick={handleEdit}
        handleDelete={handleDelete}
        handleCopy={handleCopy}
        handleChangeOwner={handleChangeOwnerClick}
        transferPermission={transferPermission}
        createPermission={createPermission}
        updatePermission={updatePermission}
        deletePermission={deletePermission}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        paginationParams={paginationParams}
        totalRecords={totalRecords}
        handlePageChange={handlePageChange}
        updatePageSize={updatePageSize}
      />
      {selectedListItem != null && (
        <CopyModal
          showModal={selectedListItem != null}
          closeModal={() => setSelectedListItem(null)}
          onConfirm={createCopy}
          listName={selectedListItem?.name}
          setCopyListName={(name: string) => setSelectedListItem({ id: selectedListItem.id, name })}
          showIndicator={copyInProgress}
        />
      )}
      {showCreateDialog && (
        <CreateListDialog
          id="create-list-dialog"
          dialogHeader="Create List"
          labelName="List Name"
          onClose={closeCreateListDialog}
          onSubmit={handleSave}
          message="Creating your list ..."
          key="create-list-dialog"
          showIndicator={createListInProgress}
        />
      )}
      {changeOwnerTarget != null && (
        <ChangeOwnerDialog
          id="list-change-owner-dialog"
          dialogHeader={CHANGE_OWNER}
          labelName="Select owner"
          onClose={() => setChangeOwnerTarget(null)}
          onSubmit={handleChangeOwnerSubmit}
          showIndicator={changeOwnerInProgress}
        />
      )}
    </>
  );
};

export default ListsView;
